/**
 * The detail tier's frame.
 *
 * The detail call is the BILLED one, so this shell exists to make the extra
 * payload visible — not to restate what the list row already said. That payload
 * used to sit in a 268px aside beside the main column; it now sits IN the
 * content column with everything else, because one item split across two
 * columns is two half-readings of one thing, and the narrow lane sized its own
 * contents from the lane rather than from the content.
 *
 * A single column also retires the whole class of width arithmetic the split
 * carried: the stand-down floor, the wrapping line, and the case where a 268px
 * aside inside a 264px Cards drill was wider than its own container and left
 * the main column with nothing. There is no line to wrap, so there is nothing
 * to arbitrate — at any container width, on any pillar.
 *
 * At Mobile a typed block is still a disclosure rather than another screen of
 * column: a detail that ends in four unlabelled paragraphs on a phone has moved
 * its own furniture into the reader's way. At Wide each block is an open titled
 * section, in the same rhythm as the column around it.
 *
 * It ends in exactly one full-width primary CTA — which may carry its own
 * alternates and its own peers, because "one primary" is a statement about
 * emphasis, not a claim that a detail offers one thing to do. The blocks sit
 * ABOVE it: the terminal control stays terminal.
 *
 * `frame` is what lets this be a set's drill pane. `Embedded` drops the tray
 * and the header, both of which the set has already drawn; without it the pane
 * is a bezel inside a bezel, and the drill has two headers.
 */
"use client";
import type { ReactElement, ReactNode } from "react";
import type { Icon as TablerIcon } from "@tabler/icons-react";
import {
  CRC_NUMERIC_STYLE,
  CRC_STACK_GAP,
  CRC_TEXT_FAINT,
  CRC_TYPE_STYLE,
} from "./cresultcard.tokens";
import { useCresultcardViewport } from "./cresultcard.viewport";
import {
  CresultcardActionBarLayout,
  CresultcardDetailFrame,
  CresultcardStackDensity,
  CresultcardTextStep,
  type CresultcardActionSpec,
} from "./cresultcard.types";
import { CresultcardActionBar } from "./CresultcardActionBar";
import { CresultcardDisclosure } from "./CresultcardDisclosure";
import { CresultcardProvenanceRail } from "./CresultcardProvenanceRail";
import { CresultcardSectionHeader } from "./CresultcardSectionHeader";
import { CresultcardSetHeader } from "./CresultcardSetHeader";
import { CresultcardTray } from "./CresultcardTray";

const COLUMN_PAD = 14;
const EMBEDDED_BOUNDS_PAD_BLOCK = 8;
const EMBEDDED_BOUNDS_FONT_SIZE = 10;

export interface CresultcardDetailBlock {
  readonly title: string;
  /** The fact that makes collapsing acceptable at Mobile. */
  readonly summary: string;
  readonly content: ReactNode;
}

interface CresultcardDetailProps {
  readonly icon: TablerIcon;
  readonly label: string;
  readonly context?: ReactNode;
  /** What bounds this projection. Wraps; never truncates. */
  readonly bounds?: ReactNode;
  readonly trailing?: ReactNode;
  /**
   * A full-bleed asset above the padded column. A `Hero` frame placed inside
   * `children` is inset by the column's own padding, so `bleedsAtHero` would
   * hand it the holder's rung for a frame that does not in fact reach the
   * plate edge. This slot is the one place in a detail where it does.
   */
  readonly hero?: ReactNode;
  /** The main column: identity, headline values, the pillar's own ornament. */
  readonly children: ReactNode;
  /**
   * Everything the detail call ADDS over the list row. Never a restatement.
   *
   * These render as titled sections at the FOOT of the same column as
   * `children`, above the terminal control — not in a lane of their own.
   */
  readonly blocks?: readonly CresultcardDetailBlock[];
  /**
   * The single terminal call to action, ABSENT when the lane publishes none.
   * The action spec requires a real href or handler, so an absent action leaves
   * no inert control or explanatory footer behind.
   */
  readonly primaryAction?: CresultcardActionSpec;
  /** Other spellings of the terminal action, behind its own chevron. */
  readonly primaryAlternates?: readonly CresultcardActionSpec[];
  /** Peers of the terminal action — a different thing to do, not another route. */
  readonly secondaryActions?: readonly CresultcardActionSpec[];
  /**
   * `Embedded` drops the tray and the header, so a detail can be a set's drill
   * pane without a second bezel around the same plate.
   */
  readonly frame?: CresultcardDetailFrame;
  readonly provenance?: ReactNode;
}

export function CresultcardDetail({
  icon,
  label,
  context,
  bounds,
  trailing,
  hero,
  children,
  blocks = [],
  primaryAction,
  primaryAlternates,
  secondaryActions,
  frame = CresultcardDetailFrame.Standalone,
  provenance,
}: CresultcardDetailProps): ReactElement {
  const { isMobile } = useCresultcardViewport();

  const body = (
    <>
      {frame === CresultcardDetailFrame.Standalone && (
        <CresultcardSetHeader
          bounds={bounds}
          context={context}
          icon={icon}
          label={label}
          trailing={trailing}
        />
      )}
      <div
        data-el="Cresultcard/Detail/Surface"
        style={{
          // The tray's field grid is an absolute overlay drawn before this
          // subtree, so static content would paint under it. The lift is the
          // whole reason this wrapper exists.
          position: "relative",
          zIndex: 1,
          display: "flex",
          flexDirection: "column",
          minWidth: 0,
        }}
      >
        {hero != null && (
          <div
            data-el="Cresultcard/Detail/Hero"
            style={{ minWidth: 0 }}
          >
            {hero}
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: CRC_STACK_GAP[CresultcardStackDensity.Loose],
            minWidth: 0,
            padding: COLUMN_PAD,
          }}
        >
          {children}
          {blocks.length > 0 && (
            <div
              data-el="Cresultcard/Detail/Sections"
              style={{
                display: "flex",
                flexDirection: "column",
                // Tighter than the column's own Loose step: the added payload
                // reads as one region of the detail, not as N more sections
                // spaced exactly like the identity above them.
                gap: CRC_STACK_GAP[CresultcardStackDensity.Regular],
                minWidth: 0,
              }}
            >
              {blocks.map((block) =>
                isMobile ? (
                  <CresultcardDisclosure
                    key={block.title}
                    label={block.title}
                    summary={block.summary}
                  >
                    {block.content}
                  </CresultcardDisclosure>
                ) : (
                  <div
                    data-el="Cresultcard/Detail/Section"
                    key={block.title}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: CRC_STACK_GAP[CresultcardStackDensity.Snug],
                      minWidth: 0,
                    }}
                  >
                    <CresultcardSectionHeader
                      title={block.title}
                    />
                    {block.content}
                  </div>
                ),
              )}
            </div>
          )}
          <CresultcardActionBar
            layout={CresultcardActionBarLayout.FullWidth}
            primary={primaryAction}
            primaryAlternates={primaryAlternates}
            secondary={secondaryActions}
          />
        </div>
      </div>
      {provenance != null && (
        <CresultcardProvenanceRail>
          {provenance}
        </CresultcardProvenanceRail>
      )}
    </>
  );

  if (frame === CresultcardDetailFrame.Embedded) {
    return (
      <div
        data-el="Cresultcard/Detail/Root"
        style={{ display: "flex", flexDirection: "column", minWidth: 0 }}
      >
        {/* Embedded drops the HEADER, which is where a standalone detail's
            bounds line lives — so the bound would leave with it. The set above
            this pane has already drawn the identity; what it has not drawn is
            what THIS pane's own projection carried, so the line is redrawn on
            its own here rather than lost. */}
        {bounds != null && (
          <span
            style={{
              ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
              ...CRC_NUMERIC_STYLE,
              position: "relative",
              zIndex: 1,
              minWidth: 0,
              paddingBlock: EMBEDDED_BOUNDS_PAD_BLOCK,
              paddingInline: COLUMN_PAD,
              fontSize: EMBEDDED_BOUNDS_FONT_SIZE,
              color: CRC_TEXT_FAINT,
            }}
          >
            {bounds}
          </span>
        )}
        {body}
      </div>
    );
  }

  return <CresultcardTray texture>{body}</CresultcardTray>;
}
